<template>
  <div style="width: 100%;">
    <v-dialog v-model="exportInProgress" persistent style="z-index: 9999;">
      <div class="dialog-content">
        <h2>{{ $t('map.generator.map_is_generating') }}</h2>
        <Loading class="mb-2"/>
      </div>
    </v-dialog>

    <v-row>
      <v-col cols="12" md="4" class="pb-0">

        <Logo/>

        <v-alert icon="info" type="info">
          {{ $t('map.generator.alert') }}
        </v-alert>

        <v-row class="mt-6">
          <v-col :cols="$vuetify.display.smAndDown ? 12 : 3" md="12" class="py-0">
            <v-select
              id="building-select"
              v-model="$query.selectedBuilding"
              :items="buildingNamesWithExterior"
              :label=" $t('map.generator.building_select') "
              color="light-green darken-1"
              variant="underlined"
            />
          </v-col>
            <v-col :cols="$vuetify.display.smAndDown ? 12 : 3" md="12" class="py-0">
            <v-select
              id="floor-select"
              v-model="$query.selectedFloor"
              :items="buildingFloors"
              :disabled="disabledFloorSelect"
              :label=" $t('map.generator.floor_select') "
              color="light-green darken-1"
              variant="underlined"
            />
          </v-col>
            <v-col :cols="$vuetify.display.smAndDown ? 12 : 6" md="12" class="py-0">
            <v-autocomplete
              id="place-select"
              v-model="$query.selectedPlace"
              :items="placesNames"
              :disabled="disabledPlaceSelect"
              :label=" $t('map.generator.place_select') "
              color="light-green darken-1"
              clearable
              variant="underlined"
            />
          </v-col>
        </v-row>

        <div class="mt-5" v-if="existingPlaceFromQuery && center">

          <div class="iframe-copy mb-4">
            <v-textarea
              id="map-link"
              outlined
              :auto-grow="!isMobile"
              hide-details
              :rows="isMobile ? 1 : 2"
              :label=" $t('map.generator.link_to_map') "
              :model-value="linkToMap"
              class="mr-2"
              variant="outlined"
            />
            <v-tooltip :text="$t('map.generator.copy')">
              <template v-slot:activator="{props}">
                <v-btn @click="copyLinkToMap" v-bind="props" icon="content_copy" variant="text" size="small"></v-btn>
              </template>
            </v-tooltip>
          </div>

          <LightButton id="btn-for-html" @click="() => showCode = !showCode">{{ showCode ? this.$t('map.generator.hide_HTML_code') : this.$t('map.generator.show_HTML_code') }}</LightButton>


          <div class="iframe-copy" v-if="isSizeValid && showCode">
            <v-textarea
              id="iframe-code"
              outlined
              rows="2"
              auto-grow
              :label=" $t('map.generator.iframe_code') "
              :model-value="iframeCode"
              class="mr-2"
              variant="outlined"
            />
            <v-tooltip :text="$t('map.generator.copy')">
              <template v-slot:activator="{ props }">
                <v-btn @click="copyIframeCode" v-bind="props" icon="content_copy" variant="text" size="small"/>
              </template>
            </v-tooltip>

          </div>
        </div>
      </v-col>
      <v-col cols="12" md="8" >
        <MapWidget
          :places="mapData"
          :selected="selectedPlace !== null ? [selectedPlace] : []"
          :legend="mapLegend"
          :center="mapCenter"
          :draggable="false"
          @changeCenter="c => center = c"
          @zoom="z => zoom = z"
          @selectedObject="selectPlaceFromMap"
          ref="mapWidgetRef"
        />
        <v-row class="justify-end pt-8 pr-2">
            <v-menu left :close-on-content-click="false" v-model="exportDialogVisible">
              <template v-slot:activator="{ props }">
                <v-btn class="mr-4" v-bind="props" variant="outlined" >
                  {{ $t('map.generator.save_as_image') }}
                </v-btn>
              </template>
              <ExportSize
                  @export="exportPng"
                  @closeDialog="toggleExportDialog()"
              />
            </v-menu>
          <v-btn :class="isMobile ? 'mt-2' : ''" color="primary" rounded @click="openInNewTab(linkToMap)">{{ $t('map.generator.open_map_tab') }}</v-btn>
        </v-row>
      </v-col>
    </v-row>
  </div>

</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import Loading from "../../components/Loading";
import ExportSize from "../../components/map/ChooseExportSize";

//Refactored
import MapWidget from "../../components/map/MapWidget";
import {isInEnglish} from "@/services/Map/MapWidget";
import LightButton from "@/components/buttons/LightButton";
import Logo from "@/components/Logo.vue";

export default {

  name: "EmbeddedMapGenerator",

  components: {
      Logo,
    Loading,
    MapWidget,
    ExportSize,
    LightButton
  },

  data() {
    return {
      notNegative: value => parseInt(value, 10) >= 0 || this.$t('map.generator.not_negative'),
      mapData: [],
      placesNames: [],
      center: null,
      zoom: 16,
      exportInProgress: false,
      exportDialogVisible: false,
      iframeHeight: 300,
      iframeWidth: 500,
      showCode: false,
    }
  },

    created() {
        if (isNaN(this.$query.selectedPlace)) this.$query.selectedPlace = null
    },

  async mounted() {
    if (isInEnglish()) this.$i18n.locale = 'en';
    await this.fetchAllBuildings()
    await  this.loadBuildingFloors()


    // if there non-existing building and is not exterior, set default = exterior
    if (!this.existingBuildingFromQuery && !this.exteriorSelected) {
      this.$query.selectedBuilding = -1
    }

    if (this.exteriorSelected) {
      this.$query.selectedFloor = null
      await this.fetchPlacesForExterior()

    } else { // building

      if (this.selectedFloor === null){
        await this.autoSelectEntranceFloor()
      } else {
        await this.fetchPlacesByFloor()
      }
    }
    this.showFloorPlaces()

    // workaround to set map-widget size to 100% after loading, but then make it resizable with iFrameHeight and iFrameWidth
    this.iframeHeight = document.getElementById("map-widget").offsetHeight;
    this.iframeWidth  = document.getElementById("map-widget").offsetWidth;

  },

  computed: {
    ...mapGetters('floors', ['getBuildingFloors']),
    ...mapGetters('buildings', ['isBuildingLoading', 'getBuildings', 'getSingleBuilding']),
    ...mapGetters('places', ['placesByFloor', 'getTypeName', 'placeById', 'getPlaces', 'didLoadedPlaces']),

      isMobile() {
        return this.$vuetify.display.xs
      },

    buildingNamesWithExterior() {
      let buildings = this.getBuildings.map(building => ({
        title: building.localizedAttributes.cz.name,
        value: building.id
      }));
      buildings.unshift(({title: this.$t('map.defaults.exterior'), value: -1}));
      return buildings;
    },

    isSizeValid() {
      return parseInt(this.iframeWidth, 10) >= 0 && parseInt(this.iframeHeight, 10) >= 0
    },
    
    exteriorSelected(){
      return this.$query.selectedBuilding === -1
    },

    place() {
      return this.placeById(this.selectedPlace);
    },

    existingPlaceFromQuery(){
      return this.selectedPlace !== null && this.place
    },

    existingBuildingFromQuery(){
      return this.selectedBuilding !== null && this.building
    },

    existingFloorFromQuery(){
      return this.selectedFloor !== null && this.floor
    },

    floor(){
      return this.buildingFloors.find(f => f.value === this.selectedFloor)
    },

    selectedPlace(){
      // use this computed property for reading value of this.$query.selectedPlace for NaN -> null conversion
      // (this.$query.selectedPlace cannot store null value)
      return Number.isInteger(this.$query.selectedPlace) ? this.$query.selectedPlace : null
    },

    selectedFloor(){
      // use this computed property for reading value of this.$query.selectedPlace for NaN -> null conversion
      // (this.$query.selectedFloor cannot store null value)
      return Number.isInteger(this.$query.selectedFloor) ? this.$query.selectedFloor : null
    },

    selectedBuilding(){
      // use this computed property for reading value of this.$query.selectedPlace for NaN -> null conversion
      // (this.$query.selectedBuilding cannot store null value)
      return Number.isInteger(this.$query.selectedBuilding) ? this.$query.selectedBuilding : null
    },

    building() {
      return this.getBuildings.find(building => building.id === this.selectedBuilding);
    },

    buildingFloors() {
      return this.getBuildingFloors(this.selectedBuilding).map(floor => ({
        title: floor.localizedAttributes.cz.name,
        value: floor.id,
      }))
    },

    hasFloors() {
      return this.buildingFloors.length > 0;
    },

    linkToMap() {
      return `${window.location.protocol}//${window.location.host}/mapwidget/embed?` +
          `building=${this.selectedBuilding}&floor=${this.selectedFloor}&place=${this.selectedPlace}&` +
          `center=${this.center.lat.toFixed(6) },${this.center.lon.toFixed(6) }&zoom=${Math.floor(this.zoom)}`;
    },

    iframeCode() {
      return `<iframe width="${this.iframeWidth}" height="${this.iframeHeight}" src="${this.linkToMap}"/>`;
    },

    disabledFloorSelect() {
      return  this.selectedBuilding === null || !this.hasFloors || this.exteriorSelected
    },

    disabledPlaceSelect() {
      return this.selectedFloor === null && this.hasFloors;
    },

    mapCenter() {
      // provided place id in query and places hasn't loaded yet
      if (this.selectedPlace !== null && !this.didLoadedPlaces) {
        return null
      }

      // provided valid place id
      if (this.existingPlaceFromQuery && this.place) {
        return this.place.position
      }

      // provided non-existing place id
      if (!this.existingPlaceFromQuery  && this.building) {
        return this.building.outline
      }

      // if floor id is provided, use building outline
      if (this.selectedFloor !== null && this.building) {
        return this.building.outline
      }
      
      return null

    },

    mapLegend() {
      let legend = null

      if (this.exteriorSelected) return

      if (this.building) {
        legend = this.$t('map.legend.building_name', { building: this.building.localizedAttributes.cz.name })
      }

      if (this.existingFloorFromQuery){
        legend += this.$t('map.legend.floor_name', { floor: this.floor.title })
      }

      return legend;
    },

    currentLanguage() {
      return isInEnglish() ? 'en' : 'cz';
    }

  },

  watch: {
    async '$route.query.selectedBuilding'() {
        this.mapData = []
        this.placesNames = []
        this.$query.selectedFloor = null
        this.$query.selectedPlace = null
        this.clearFloors();

        if (this.exteriorSelected) {
          await this.fetchPlacesForExterior()
          this.showFloorPlaces();

        } else { // building
          this.loadBuildingFloors();
          if (this.selectedFloor === null){
            await this.autoSelectEntranceFloor()
            // places are shown thanks to watcher for selectedFloor
          }
        }
    },

    '$route.query.selectedFloor' : {
      async handler() {
        this.$query.selectedPlace = null
        await this.fetchPlacesByFloor()
        this.showFloorPlaces();
      },
    },
  },

  methods: {
    ...mapActions('floors', ['fetchFloorById', 'clearFloors']),
    ...mapActions('buildings', ['fetchAllBuildings']),
    ...mapActions('places', ['fetchFilteredPlaces', 'fetchFloor']),

    async fetchPlacesByFloor(){
      if (this.selectedFloor !== null) {
        await this.fetchFilteredPlaces({
          floorId: this.selectedFloor,
          buildingId: null,
          exterior: null,
          language: null,
          placeName: null
        });
      }

    },

    async fetchPlacesForExterior(){
      await this.fetchFilteredPlaces({
        floorId: null,
        buildingId: null,
        exterior: true,
        language: null,
        placeName: null
      });
    },

    selectPlaceFromMap(mapObject) {
      this.$query.selectedPlace = mapObject.id;
    },

    loadBuildingFloors() { // items for selection
      if (this.building) {
        this.building.floorIds.map(floor => this.fetchFloorById(floor));
      }
    },

    async autoSelectEntranceFloor() {
      this.$query.selectedFloor = this.building.defaultFloor;
    },

    invalidateSize() {
      if (this.$refs.mapWidgetRef) {
        this.$refs.mapWidgetRef.map.invalidateSize();
      }
    },

    showFloorPlaces() {
      this.mapData = this.placesByFloor(this.selectedFloor).map(o => ({
        id: o.id,
        type: o.type,
        position: o.position,
        searchable: o.searchable,
        name: o.localizedAttributes[this.currentLanguage]?.nickname || o.localizedAttributes[this.currentLanguage]?.name,
      }));

      const placesNames = this.mapData.filter(p => p.searchable && p.name).map(p => ({
        value: p.id,
        title: p.name
      }));
      placesNames.sort((a, b) => a.title.localeCompare(b.title));
      this.placesNames = placesNames;
    },

    async copyIframeCode() {
      const html = this.iframeCode;
      await navigator.clipboard.writeText(html);
    },

    async copyLinkToMap() {
      const link = this.linkToMap;
      await navigator.clipboard.writeText(link);
    },

    toggleExportDialog() {
      this.exportDialogVisible = !this.exportDialogVisible;
    },

    exportPng(size = 0) {
      this.toggleExportDialog();
      this.$refs.mapWidgetRef.exportPng(size);
      this.exportInProgress = true;
      this.exportTimer = setInterval(() => {

        if (document.getElementsByClassName('epHolder') &&
            document.getElementsByClassName('epHolder')[0]) {

          setTimeout(() => {
            this.exportInProgress = false;
          }, 1500);
          clearInterval(this.exportTimer);
        }
      }, 1000)
    },

    openInNewTab(link) {
      window.open(link, '_blank', 'noreferrer');
    },

  },

}
</script>

<style scoped>
.content {
  padding: 1.5rem 4rem 2rem 4rem;
}

.map-container {
  margin: 0 auto;
  width: 100%;
  max-width: 850px;
  display: flex;
  flex-direction: column;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 320ms;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.map-component {
  z-index: 1;
}

#map-widget {
  z-index: 1;
  height: auto;
  flex-grow: 1;
  height: 78vh;
  margin: 0;
  border-radius: 8px;
}

@media only screen and (max-width: 600px) {
    #map-widget {
        height: 60vh;
    }
}

.iframe-copy {
  display: flex;
  align-items: flex-start;
}

.map-printing {
  position: relative;
  top: 60px;
}

.dialog-content {
  background-color: whitesmoke;
  text-align: center;
  height: 90vh;
  padding-top: 35vh;
}

</style>
